/**
 * Small geometry shared by the diagram layouts: rectangles, overlap tests, a seeded
 * random source and a spatial index for "does this box hit anything placed so far?".
 *
 * Every diagram is laid out as pure arithmetic in viewBox units, so the same input and
 * the same seed always produce the same picture — on the server, in the browser and in
 * the tests.
 */

import type { Rect } from "@/lib/tool-kit/charts/layout";

/** True when two rectangles share any area, after growing both by `gap`. */
export function rectsOverlap(a: Rect, b: Rect, gap = 0): boolean {
  return (
    a.x < b.x + b.width + gap &&
    b.x < a.x + a.width + gap &&
    a.y < b.y + b.height + gap &&
    b.y < a.y + a.height + gap
  );
}

/** True when `inner` lies wholly inside `outer`. */
export function rectContains(outer: Rect, inner: Rect): boolean {
  return (
    inner.x >= outer.x - 1e-9 &&
    inner.y >= outer.y - 1e-9 &&
    inner.x + inner.width <= outer.x + outer.width + 1e-9 &&
    inner.y + inner.height <= outer.y + outer.height + 1e-9
  );
}

/** The smallest rectangle around all of `rects`, or null for none. */
export function boundingRect(rects: readonly Rect[]): Rect | null {
  if (rects.length === 0) {
    return null;
  }
  let left = Number.POSITIVE_INFINITY;
  let top = Number.POSITIVE_INFINITY;
  let right = Number.NEGATIVE_INFINITY;
  let bottom = Number.NEGATIVE_INFINITY;
  for (const rect of rects) {
    left = Math.min(left, rect.x);
    top = Math.min(top, rect.y);
    right = Math.max(right, rect.x + rect.width);
    bottom = Math.max(bottom, rect.y + rect.height);
  }
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/** Pairs of indices whose rectangles overlap — the check the tests make of every layout. */
export function overlappingPairs(
  rects: readonly Rect[],
  gap = 0
): [number, number][] {
  const pairs: [number, number][] = [];
  for (let i = 0; i < rects.length; i++) {
    for (let j = i + 1; j < rects.length; j++) {
      if (rectsOverlap(rects[i], rects[j], gap)) {
        pairs.push([i, j]);
      }
    }
  }
  return pairs;
}

const PARK_MILLER_MODULUS = 2_147_483_647;
const PARK_MILLER_MULTIPLIER = 48_271;

/**
 * A deterministic random source in [0, 1) — Park–Miller's minimal standard generator.
 * Plain integer arithmetic that is exact in doubles, so a seed gives the same sequence
 * in every JavaScript engine. For layout variety, never for anything secret.
 */
export function seededRandom(seed: number): () => number {
  let state = Math.abs(Math.floor(seed)) % PARK_MILLER_MODULUS;
  if (state === 0) {
    state = 1;
  }
  return () => {
    state = (state * PARK_MILLER_MULTIPLIER) % PARK_MILLER_MODULUS;
    return (state - 1) / (PARK_MILLER_MODULUS - 1);
  };
}

/**
 * A uniform grid over the plane that remembers which placed rectangles touch each
 * cell, so a collision test looks at a handful of neighbours instead of every box
 * placed so far.
 */
export class RectIndex {
  private readonly cells = new Map<string, number[]>();
  private readonly rects: Rect[] = [];
  private readonly cellSize: number;

  constructor(cellSize = 32) {
    this.cellSize = cellSize;
  }

  private keysFor(rect: Rect): string[] {
    const size = this.cellSize;
    const keys: string[] = [];
    const x0 = Math.floor(rect.x / size);
    const x1 = Math.floor((rect.x + rect.width) / size);
    const y0 = Math.floor(rect.y / size);
    const y1 = Math.floor((rect.y + rect.height) / size);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        keys.push(`${cx},${cy}`);
      }
    }
    return keys;
  }

  add(rect: Rect): void {
    const index = this.rects.length;
    this.rects.push(rect);
    for (const key of this.keysFor(rect)) {
      const bucket = this.cells.get(key);
      if (bucket) {
        bucket.push(index);
      } else {
        this.cells.set(key, [index]);
      }
    }
  }

  /** True when `rect`, grown by `gap`, overlaps any rectangle added so far. */
  collides(rect: Rect, gap = 0): boolean {
    const grown = {
      x: rect.x - gap,
      y: rect.y - gap,
      width: rect.width + 2 * gap,
      height: rect.height + 2 * gap,
    };
    for (const key of this.keysFor(grown)) {
      for (const index of this.cells.get(key) ?? []) {
        if (rectsOverlap(grown, this.rects[index])) {
          return true;
        }
      }
    }
    return false;
  }
}
